import React from 'react';
import { Sparkles, TrendingUp, Minus, TrendingDown } from 'lucide-react';

const MatchScoreBadge = ({ score, size = 'md', showLabel = true, className = '' }) => {
  const getMatchLevel = (score) => {
    if (score >= 80) return { level: 'excellent', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50', icon: Sparkles };
    if (score >= 60) return { level: 'good', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50', icon: TrendingUp };
    if (score >= 40) return { level: 'fair', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50', icon: Minus };
    return { level: 'low', color: 'bg-gray-400', textColor: 'text-gray-600', bgLight: 'bg-gray-50', icon: TrendingDown };
  };

  const { level, color, textColor, bgLight, icon: Icon } = getMatchLevel(score);

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  return (
    <div 
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${bgLight} ${textColor} ${sizeClasses[size]} ${className}`}
      title={`Match Score: ${Math.round(score)}% (${level})`}
      data-testid="match-score-badge"
    >
      <Icon className={iconSizes[size]} />
      <span>{Math.round(score)}%</span>
      {showLabel && size !== 'sm' && (
        <span className="font-normal opacity-75 capitalize">{level}</span>
      )}
    </div>
  );
};

export const MatchScoreBreakdown = ({ breakdown }) => {
  if (!breakdown) return null;

  const factors = [
    { key: 'skills', label: 'Skills Match', icon: '🛠️' },
    { key: 'location', label: 'Location', icon: '📍' },
    { key: 'budget', label: 'Budget Fit', icon: '💰' },
    { key: 'experience', label: 'Experience', icon: '📊' },
    { key: 'rating', label: 'Rating', icon: '⭐' }
  ];

  return (
    <div className="space-y-2 p-3 bg-gray-50 rounded-lg" data-testid="match-breakdown">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Match Breakdown</p>
      {factors.map(({ key, label, icon }) => {
        const data = breakdown[key];
        if (!data) return null;
        
        return (
          <div key={key} className="flex items-center justify-between text-sm">
            <span className="text-gray-600 flex items-center gap-1">
              <span>{icon}</span>
              {label}
              <span className="text-gray-400 text-xs">({data.weight})</span>
            </span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full ${
                    data.score >= 80 ? 'bg-green-500' : 
                    data.score >= 60 ? 'bg-blue-500' : 
                    data.score >= 40 ? 'bg-amber-500' : 'bg-gray-400'
                  }`}
                  style={{ width: `${data.score}%` }}
                />
              </div>
              <span className="font-medium text-gray-800 w-10 text-right">{Math.round(data.score)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MatchScoreBadge;
